/* eslint-disable sonarjs/no-duplicate-string */
import { sanitizeURL } from '../utils/utils';

export interface ApiUrls {
  readonly container: string;
  readonly micro: string;
  readonly customers?: string;
  readonly inprogress: string;
  readonly storemgmt: string;
  readonly menu: string;
  readonly mpo: string;
  readonly inventory: string;
  readonly customer: string;
  readonly cashmgmt: string;
  readonly am: string;
  readonly payment: string;
  readonly pricing: string;
  readonly leasability: string;
}

export interface AppConfigItem {
  readonly apiUrls: ApiUrls;
  readonly microUrl: string;
  readonly auth: {
    readonly region: string;
    readonly userPoolId: string;
    readonly userPoolWebClientId: string;
    readonly oauth: {
      readonly domain: string;
      readonly scope?: string[];
      readonly redirectSignIn: string;
      readonly redirectSignOut: string;
      readonly responseType: string;
      readonly urlOpener: (url: string) => void;
    };
  };
}

const microUrl = {
  localhost: 'http://localhost:3005',
  local: 'https://local-agreement-racpad.rentacenter.com',
  dev: 'https://dev-agreement-racpad.rentacenter.com',
  qa: 'https://qa-agreement-racpad.rentacenter.com',
  uat: 'https://uat-agreement-racpad.rentacenter.com',
  prod: 'https://racpad-agreement.rentacenter.com',
};

export interface AppConfig extends AppConfigItem {
  readonly appName: string;
}

export const urlOpener = (url: string): void => {
  const urlToOpen = sanitizeURL(url);

  if (urlToOpen) {
    window.open(urlToOpen, '_self');
  }
};

export const localhost: AppConfigItem = {
  apiUrls: {
    container: 'https://dev-racpad.rentacenter.com/api',
    micro: 'https://dev-agreement-racpad.rentacenter.com/api',
    customers: 'https://dev-customers-racpad.rentacenter.com/api',
    inprogress: 'https://dev-agreement-racpad.rentacenter.com/api',
    storemgmt: 'https://dev-storemgmt-racpad.rentacenter.com/api',
    menu: 'https://dev-menu-racpad.rentacenter.com/api',
    mpo: 'https://dev-mpo-racpad.rentacenter.com/api',
    inventory: 'https://dev-inventory-racpad.rentacenter.com/api',
    customer: 'https://dev-customer-racpad.rentacenter.com/api',
    cashmgmt: 'https://dev-cashmgmt-racpad.rentacenter.com/api',
    am: 'https://dev-am-racpad.rentacenter.com/api',
    payment: 'https://dev-payment1-racpad.rentacenter.com/api',
    pricing: 'https://dev-pricing-racpad.rentacenter.com/api',
    leasability: 'https://dev-leasability-lb.rentacenter.com',
  },
  microUrl: microUrl.localhost,
  auth: {
    region: 'us-east-1',
    // for DEV use
    userPoolId: 'us-east-1_PAFbhRAvs',
    userPoolWebClientId: 'lp0bccsjvfmvls52nmlsu1jva',

    // for QA use
    // userPoolId: 'us-east-1_DygwZ1Aum',
    // userPoolWebClientId: '6en81tjat6j6naq837ddu5aoov',
    oauth: {
      // for QA use
      // domain: 'auth-qa2-racpad.auth.us-east-1.amazoncognito.com',
      // for DEV use
      domain: 'auth-dev2-racpad.auth.us-east-1.amazoncognito.com',
      redirectSignIn: microUrl.localhost,
      redirectSignOut: microUrl.localhost,
      responseType: 'token',
      urlOpener,
    },
  },
};

export const localhostIE11: AppConfigItem = { ...localhost };

export const local: AppConfigItem = {
  apiUrls: {
    container: 'https://qa-racpad.rentacenter.com/api',
    micro: 'https://qa-agreement-racpad.rentacenter.com/api',
    customers: 'https://qa-customers-racpad.rentacenter.com/api',
    inprogress: 'https://qa-agreement-racpad.rentacenter.com/api',
    storemgmt: 'https://qa-storemgmt-racpad.rentacenter.com/api',
    menu: 'https://qa-menu-racpad.rentacenter.com/api',
    mpo: 'https://qa-mpo-racpad.rentacenter.com/api',
    inventory: 'https://qa-inventory-racpad.rentacenter.com/api',
    customer: 'https://qa-customer-racpad.rentacenter.com/api',
    cashmgmt: 'https://qa-cashmgmt-racpad.rentacenter.com/api',
    am: 'https://qa-am-racpad.rentacenter.com/api',
    payment: 'https://qa-payment1-racpad.rentacenter.com/api',
    pricing: 'https://local-pricing-racpad.rentacenter.com/api',
    leasability: 'https://dev-leasability-lb.rentacenter.com',
  },
  microUrl: microUrl.local,
  auth: {
    region: 'us-east-1',
    userPoolId: 'us-east-1_FPWut7aB',
    userPoolWebClientId: '608s8dso4aecka8omjpukssq3n',
    oauth: {
      domain: 'auth-local-racpad.auth.us-east-1.amazoncognito.com',
      redirectSignIn: microUrl.local,
      redirectSignOut: microUrl.local,
      responseType: 'token',
      urlOpener,
    },
  },
};

export const dev: AppConfigItem = {
  apiUrls: {
    container: 'https://dev-racpad.rentacenter.com/api',
    micro: 'https://dev-agreement-racpad.rentacenter.com/api',
    customers: 'https://dev-customers-racpad.rentacenter.com/api',
    inprogress: 'https://dev-agreement-racpad.rentacenter.com/api',
    storemgmt: 'https://dev-storemgmt-racpad.rentacenter.com/api',
    menu: 'https://dev-menu-racpad.rentacenter.com/api',
    mpo: 'https://dev-mpo-racpad.rentacenter.com/api',
    inventory: 'https://dev-inventory-racpad.rentacenter.com/api',
    customer: 'https://dev-customer-racpad.rentacenter.com/api',
    cashmgmt: 'https://dev-cashmgmt-racpad.rentacenter.com/api',
    am: 'https://dev-am-racpad.rentacenter.com/api',
    payment: 'https://dev-payment1-racpad.rentacenter.com/api',
    pricing: 'https://dev-pricing-racpad.rentacenter.com/api',
    leasability: 'https://dev-leasability-lb.rentacenter.com',
  },
  microUrl: microUrl.dev,
  auth: {
    region: 'us-east-1',
    userPoolId: 'us-east-1_8MbOyEYG6',
    userPoolWebClientId: '47r2ihk9fehcpn9t64thdbu2tl',
    oauth: {
      domain: 'auth-qa-racpad.auth.us-east-1.amazoncognito.com',
      redirectSignIn: microUrl.dev,
      redirectSignOut: microUrl.dev,
      responseType: 'token',
      urlOpener,
    },
  },
};

export const qa: AppConfigItem = {
  apiUrls: {
    container: 'https://qa-racpad.rentacenter.com/api',
    micro: 'https://qa-agreement-racpad.rentacenter.com/api',
    customers: 'https://qa-customers-racpad.rentacenter.com/api',
    inprogress: 'https://dev-agreement-racpad.rentacenter.com/api',
    storemgmt: 'https://qa-storemgmt-racpad.rentacenter.com/api',
    menu: 'https://dev-qa-racpad.rentacenter.com/api',
    mpo: 'https://qa-mpo-racpad.rentacenter.com/api',
    inventory: 'https://qa-inventory-racpad.rentacenter.com/api',
    customer: 'https://qa-customer-racpad.rentacenter.com/api',
    cashmgmt: 'https://qa-cashmgmt-racpad.rentacenter.com/api',
    am: 'https://qa-am-racpad.rentacenter.com/api',
    payment: 'https://qa-payment1-racpad.rentacenter.com/api',
    pricing: 'https://qa-pricing-racpad.rentacenter.com/api',
    leasability: 'https://dev-leasability-lb.rentacenter.com',
  },
  microUrl: microUrl.qa,
  auth: {
    region: 'us-east-1',
    userPoolId: 'us-east-1_8MbOyEYG6',
    userPoolWebClientId: '47r2ihk9fehcpn9t64thdbu2tl',
    oauth: {
      domain: 'auth-qa-racpad.auth.us-east-1.amazoncognito.com',
      redirectSignIn: microUrl.qa,
      redirectSignOut: microUrl.qa,
      responseType: 'token',
      urlOpener,
    },
  },
};

export const uat: AppConfigItem = {
  apiUrls: {
    container: 'https://uat-racpad.rentacenter.com/api',
    micro: 'https://uat-agreement-racpad.rentacenter.com/api',
    customers: 'https://uat-customers-racpad.rentacenter.com/api',
    inprogress: 'https://dev-agreement-racpad.rentacenter.com/api',
    storemgmt: 'https://uat-storemgmt-racpad.rentacenter.com/api',
    menu: 'https://uat-menu-racpad.rentacenter.com/api',
    mpo: 'https://uat-mpo-racpad.rentacenter.com/api',
    inventory: 'https://uat-inventory-racpad.rentacenter.com/api',
    customer: 'https://uat-customer-racpad.rentacenter.com/api',
    cashmgmt: 'https://uat-cashmgmt-racpad.rentacenter.com/api',
    am: 'https://uat-am-racpad.rentacenter.com/api',
    payment: 'https://uat-payment1-racpad.rentacenter.com/api',
    pricing: 'https://uat-pricing-racpad.rentacenter.com/api',
    leasability: 'https://dev-leasability-lb.rentacenter.com',
  },
  microUrl: microUrl.uat,
  auth: {
    region: 'us-east-1',
    userPoolId: 'us-east-1_FBmCthpda',
    userPoolWebClientId: '1d8vgr67fr7gli9abpv9j1cb3n',
    oauth: {
      domain: 'auth-uat-racpad.auth.us-east-1.amazoncognito.com',
      redirectSignIn: microUrl.uat,
      redirectSignOut: microUrl.uat,
      responseType: 'token',
      urlOpener,
    },
  },
};

export const prod: AppConfigItem = {
  apiUrls: {
    container: 'https://racpad.rentacenter.com/api',
    micro: 'https://racpad-agreement.rentacenter.com/api',
    customers: 'https://racpad-customers.rentacenter.com/api',
    inprogress: 'https://dev-agreement-racpad.rentacenter.com/api',
    storemgmt: 'https://storemgmt-racpad.rentacenter.com/api',
    menu: 'https://menu-racpad.rentacenter.com/api',
    mpo: 'https://mpo-racpad.rentacenter.com/api',
    inventory: 'https://inventory-racpad.rentacenter.com/api',
    customer: 'https://customer-racpad.rentacenter.com/api',
    cashmgmt: 'https://cashmgmt-racpad.rentacenter.com/api',
    am: 'https://am-racpad.rentacenter.com/api',
    payment: 'https://payment1-racpad.rentacenter.com/api',
    pricing: 'https://racpad-pricing.rentacenter.com/api',
    leasability: 'https://leasability-lb.rentacenter.com',
  },
  microUrl: microUrl.prod,
  auth: {
    region: 'us-east-1',
    userPoolId: 'us-east-1_so5RR57Of',
    userPoolWebClientId: '50nk6ibf5lru47hesk4j0cfuu4',
    oauth: {
      domain: 'auth-racpad.auth.us-east-1.amazoncognito.com',
      redirectSignIn: microUrl.prod,
      redirectSignOut: microUrl.prod,
      responseType: 'token',
      urlOpener,
    },
  },
};

export const artifact: AppConfigItem = {
  apiUrls: {
    container: '_apiUrls.container',
    micro: '_apiUrls.micro',
    inprogress: 'https://dev-agreement-racpad.rentacenter.com/api',
    storemgmt: '_apiUrls.storemgmt',
    menu: '_apiUrls.menu',
    mpo: '_apiUrls.mpo',
    inventory: '_apiUrls.inventory',
    customer: '_apiUrls.customer',
    cashmgmt: '_apiUrls.cashmgmt',
    am: '_apiUrls.am',
    payment: '_apiUrls.payment1',
    pricing: '_apiUrls.pricing',
    leasability: '_apiUrls.leasability',
  },
  microUrl: '_microUrl',
  auth: {
    region: '_auth.region',
    userPoolId: '_auth.userPoolId',
    userPoolWebClientId: '_auth.userPoolWebClientId',
    oauth: {
      domain: '_auth.oauth.domain',
      redirectSignIn: '_auth.oauth.redirectSignIn',
      redirectSignOut: '_auth.oauth.redirectSignOut',
      responseType: '_auth.oauth.responseType',
      urlOpener,
    },
  },
};

let appConfigItem: AppConfigItem;

switch (process.env.REACT_APP_STAGE) {
  case 'localhost':
    appConfigItem = localhost;
    break;
  case 'localhost-ie11':
    appConfigItem = localhostIE11;
    break;
  case 'local':
    appConfigItem = local;
    break;
  case 'dev':
    appConfigItem = dev;
    break;
  case 'qa':
    appConfigItem = qa;
    break;
  case 'uat':
    appConfigItem = uat;
    break;
  case 'prod':
    appConfigItem = prod;
    break;
  case 'artifact':
    appConfigItem = artifact;
    break;
  default:
    appConfigItem = localhost;
    break;
}

export const appConfig: AppConfig = {
  appName: 'Agreement',
  ...appConfigItem,
};
